import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed } from "lucide-react";
import { toast } from "sonner";

export const CallButton = ({ phone, leadId, practiceId, contactName, testid = "romulus-call-btn", size = "default", className = "" }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!phone) return null;
  if (!user?.romulus_extension) {
    return <a href={`tel:${phone}`}><Button variant="outline" size={size} className={className} data-testid={testid}><Phone className="h-4 w-4 mr-2" />Chiama</Button></a>;
  }
  const call = async () => {
    setBusy(true);
    try {
      await api.post("/telefonia/call", { phone, lead_id: leadId, practice_id: practiceId, contact_name: contactName });
      toast.success(`Sta squillando il tuo interno ${user.romulus_extension}: rispondi e partirà la chiamata`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chiamata non avviata"); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" size={size} className={className} onClick={call} disabled={busy} data-testid={testid}>
      <Phone className="h-4 w-4 mr-2 text-emerald-600" />{busy ? "Chiamata…" : "Chiama dal centralino"}
    </Button>
  );
};

const fmtDur = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, "0")}`;
const LABELS = { ANSWERED: "Risposta", COMPLETED: "Conclusa", NO_ANSWER: "Nessuna risposta", BUSY: "Occupato", FAILED: "Fallita", INITIATED: "Avviata", CANCELLED: "Annullata", MISSED: "Persa" };

export const CallLog = ({ leadId, practiceId }) => {
  const [calls, setCalls] = useState(null);
  useEffect(() => {
    const params = leadId ? { lead_id: leadId } : { practice_id: practiceId };
    api.get("/telefonia/calls", { params }).then((r) => setCalls(r.data)).catch(() => setCalls([]));
  }, [leadId, practiceId]);
  if (!calls || !calls.length) return null;
  return (
    <Card className="p-5 space-y-3" data-testid="call-log">
      <h2 className="font-heading font-semibold flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> Chiamate ({calls.length})</h2>
      <div className="divide-y divide-border">
        {calls.map((c) => {
          const inbound = c.direction === "INBOUND";
          const missed = !c.duration && c.status && c.status !== "INITIATED";
          const Icon = missed ? PhoneMissed : inbound ? PhoneIncoming : PhoneOutgoing;
          return (
            <div key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm" data-testid={`call-row-${c.id}`}>
              <Icon className={`h-4 w-4 ${missed ? "text-rose-500" : inbound ? "text-sky-600" : "text-emerald-600"}`} />
              <span className="font-medium">{inbound ? "In arrivo" : "In uscita"}</span>
              <span className="text-muted-foreground tabular-nums">{c.initiated_at ? new Date(c.initiated_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}</span>
              <span className="tabular-nums">{fmtDur(c.duration)}</span>
              <span className="text-xs rounded-full bg-muted px-2 py-0.5">{LABELS[c.status] || c.status || "—"}</span>
              <span className="text-xs text-muted-foreground">{inbound ? c.callee_label : (c.crm_user_name || c.caller_label)}</span>
              {c.recording_url && <a href={c.recording_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline" data-testid={`call-rec-${c.id}`}>Registrazione</a>}
            </div>
          );
        })}
      </div>
    </Card>
  );
};
